import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { configureHttp } from '@/core/api/http';
import { storage } from '@/core/storage/storage';

interface AuthState {
  accessToken: string | null;
  phoneNumber: string | null;
  /** Controls the global login modal. */
  loginOpen: boolean;
  /** What to do after a successful login (e.g. resume the payment). */
  onLoggedIn: (() => void) | null;
  setSession: (token: string, phone: string) => void;
  logout: () => void;
  openLogin: (onLoggedIn?: () => void) => void;
  closeLogin: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      phoneNumber: null,
      loginOpen: false,
      onLoggedIn: null,
      setSession: (accessToken, phoneNumber) => set({ accessToken, phoneNumber }),
      logout: () => {
        storage.clearAll();
        set({ accessToken: null, phoneNumber: null });
      },
      openLogin: (onLoggedIn) => set({ loginOpen: true, onLoggedIn: onLoggedIn ?? null }),
      closeLogin: () => set({ loginOpen: false, onLoggedIn: null }),
    }),
    {
      name: 'blino:auth',
      partialize: (s) => ({ accessToken: s.accessToken, phoneNumber: s.phoneNumber }),
    },
  ),
);

// Wire the HTTP layer to the store once; `core/` never imports from `features/`.
configureHttp({
  getToken: () => useAuthStore.getState().accessToken,
  onUnauthorized: () => useAuthStore.getState().logout(),
});

export const useIsLoggedIn = () => useAuthStore((s) => Boolean(s.accessToken));

/** Returns a guard: runs `action` now if logged in, otherwise opens login first. */
export function useRequireAuth() {
  const openLogin = useAuthStore((s) => s.openLogin);
  return (action: () => void) => {
    if (useAuthStore.getState().accessToken) action();
    else openLogin(action);
  };
}
